import React from 'react';
import { Heart } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * WishlistButton Component
 * Toggle button to add/remove products from wishlist
 */
const WishlistButton = ({ 
  product, 
  variant = 'icon', // 'icon' | 'button'
  size = 'default', // 'small' | 'default' | 'large'
  className = ''
}) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  
  if (!product) return null;
  
  const inWishlist = isInWishlist(product.id);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const added = toggleWishlist(product);
    
    if (added) {
      toast.success('Added to wishlist', {
        description: product.name
      });
    } else {
      toast('Removed from wishlist', {
        description: product.name
      });
    }
  };

  const sizeClasses = {
    small: 'h-8 w-8',
    default: 'h-10 w-10',
    large: 'h-12 w-12'
  };

  const iconSizes = {
    small: 'h-4 w-4',
    default: 'h-5 w-5',
    large: 'h-6 w-6'
  };

  if (variant === 'button') {
    return (
      <Button
        variant={inWishlist ? 'default' : 'outline'}
        onClick={handleClick}
        className={`gap-2 ${className}`}
        style={inWishlist ? { background: 'var(--brand)' } : {}}
        data-testid={`wishlist-btn-${product.id}`}
      >
        <Heart 
          className={iconSizes[size]} 
          fill={inWishlist ? 'currentColor' : 'none'} 
        />
        {inWishlist ? 'In Wishlist' : 'Add to Wishlist'}
      </Button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={`
        ${sizeClasses[size]} 
        rounded-full flex items-center justify-center transition-all
        ${inWishlist 
          ? 'bg-red-50 text-red-500 hover:bg-red-100' 
          : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
        }
        shadow-sm border border-gray-100
        ${className}
      `}
      title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      data-testid={`wishlist-icon-${product.id}`}
    >
      <Heart 
        className={iconSizes[size]} 
        fill={inWishlist ? 'currentColor' : 'none'} 
      />
    </button>
  );
};

export default WishlistButton;
